import { useRootFiIntegrations } from '@commenda/api';
import { ROOTFI_FULL_SUITE_USE_CASES } from '@commenda/types';
import { RootfiCompanyUseCase } from '@prisma/client';
import { useCallback, useMemo, useState } from 'react';

import { RootFiInviteLinkConnectModal } from '../../root-fi';
import { useRootFiIntegration } from '../../root-fi/hooks/useRootfiIntegration';

import type {
  SalesTaxRootFiConnectFlowArgs,
  SalesTaxRootFiConnectFlowResult,
} from './types';

/**
 * Sales-tax RootFi connect flow: company list plus the invite-link modal that
 * creates a link and hands it to the RootFi SDK.
 */
export function useSalesTaxRootFiConnectFlow({
  corporationId,
  setNotification,
  onRootFiConnectSuccess,
}: SalesTaxRootFiConnectFlowArgs): SalesTaxRootFiConnectFlowResult {
  const {
    data: integrations,
    mutate: mutateIntegrations,
    isLoading: isIntegrationsLoading,
  } = useRootFiIntegrations(corporationId, RootfiCompanyUseCase.SALES_TAX);

  const [connectModalOpen, setConnectModalOpen] = useState(false);

  const onError = useCallback(
    (message: string) => {
      setNotification({
        show: true,
        title: 'Connection Attempt Failed',
        message,
        valid: false,
      });
    },
    [setNotification]
  );

  const { openRootfiLink, isRootfiReady } = useRootFiIntegration({
    corporationId,
    onSuccess: onRootFiConnectSuccess,
    refetchIntegrations: () => {
      void mutateIntegrations();
    },
    onError,
  });

  const handleConnectIntegrationClick = useCallback(() => {
    setConnectModalOpen(true);
  }, []);

  const onInviteLinkSaved = useCallback(
    (inviteLinkId: string) => {
      void mutateIntegrations();
      openRootfiLink(inviteLinkId);
      setConnectModalOpen(false);
    },
    [mutateIntegrations, openRootfiLink]
  );

  const connectRootFiIntegrationModal = useMemo(
    () => (
      <RootFiInviteLinkConnectModal
        corporationId={corporationId}
        useCases={ROOTFI_FULL_SUITE_USE_CASES}
        isOpen={connectModalOpen}
        onClose={() => setConnectModalOpen(false)}
        onInviteLinkSaved={onInviteLinkSaved}
      />
    ),
    [corporationId, connectModalOpen, onInviteLinkSaved]
  );

  return {
    integrations,
    isRootFiDataLoading: isIntegrationsLoading,
    isRootfiReady,
    openRootfiLink,
    handleConnectIntegrationClick,
    connectRootFiIntegrationModal,
    refetchRootFiIntegrations: () => {
      void mutateIntegrations();
    },
  };
}
